import {
  Attachments,
  Bubble,
  Conversations,
  Prompts,
  Sender,
  useXAgent,
  useXChat,
} from "@ant-design/x";

import {
  senderPromptsItems,
  defaultConversationsItems,
} from "../components/items";

import {
  CloudUploadOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PaperClipOutlined,
  PlusOutlined,
  LoginOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

import {
  Typography,
  Space,
  Button,
  Drawer,
  message,
  Card,
  Modal,
} from "antd";

import { useNavigate } from "react-router-dom";
import { Badge, type GetProp } from "antd";
import { useEffect, useState, useRef } from "react";
import { XStream } from "@ant-design/x";
import markdownit from "markdown-it";

import useAppStyles from "../styles/AppStyles";
import ConversationItem from "../components/conversation";

// 问题数据接口
interface QuestionData {
  question: string;
  options: string[];
}

// 初始化markdown解析器
const md = markdownit({ html: true, breaks: true });

// 解析问题内容的函数
const parseQuestionContent = (content: string): QuestionData | null => {
  try {
    // 使用正则表达式解析问题和选项
    const questionMatch = content.match(/<Question>([\s\S]*?)<\/Question>/);
    if (!questionMatch) return null;

    const innerContent = questionMatch[1];
    
    // 提取问题文本（<Option>之前的内容）
    const questionText = innerContent.split('<Option>')[0].trim();
    
    // 提取所有选项
    const optionMatches = innerContent.match(/<Option>([\s\S]*?)(?=<Option>|$)/g);
    const options = optionMatches 
      ? optionMatches.map(match => match.replace('<Option>', '').replace('</Option>', '').trim()).filter(opt => opt.length > 0)
      : [];

    return {
      question: questionText,
      options: options
    };
  } catch (error) {
    console.error('解析问题内容失败:', error);
    return null;
  }
};

// 问题卡片组件
const QuestionCard: React.FC<{
  questionData: QuestionData;
  onOptionClick: (option: string) => void;
  onClose: () => void;
  onManualInput: () => void;
}> = ({ questionData, onOptionClick, onClose, onManualInput }) => {
  return (
    <Modal
      title={
        <div style={{ 
          fontSize: '18px', 
          fontWeight: 'bold',
          color: '#1890ff',
          textAlign: 'center'
        }}>
          🤔 请选择您的回答
        </div>
      }
      open={true}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      bodyStyle={{ padding: '24px' }}
    >
      <Card 
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)',
          boxShadow: 'none'
        }}
      >
        <Typography.Title 
          level={4} 
          style={{ 
            marginBottom: 32,
            textAlign: 'center',
            color: '#262626',
            fontWeight: 500,
            lineHeight: 1.6
          }}
        >
          {questionData.question}
        </Typography.Title>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {questionData.options.map((option, index) => (
            <Button
              key={index}
              block
              size="large"
              style={{
                textAlign: 'left',
                height: 'auto',
                minHeight: '48px',
                padding: '16px 20px',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                borderRadius: '12px',
                border: '1px solid #e8f4fd',
                background: '#ffffff',
                fontSize: '15px',
                color: '#262626',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f9ff';
                e.currentTarget.style.borderColor = '#1890ff';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(24,144,255,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e8f4fd';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
              }}
              onClick={() => onOptionClick(option)}
            >
              <span style={{ marginRight: '8px', fontWeight: 'bold', color: '#1890ff' }}>
                {String.fromCharCode(65 + index)}.
              </span>
              {option}
            </Button>
          ))}
          
          {/* 手动输入选项 */}
          <Button
            block
            size="large"
            type="dashed"
            style={{
              height: '48px',
              borderRadius: '12px',
              fontSize: '15px',
              color: '#8c8c8c',
              borderColor: '#d9d9d9',
              background: '#fafafa'
            }}
            onClick={onManualInput}
          >
            ✏️ 手动输入其他答案
          </Button>
        </Space>
      </Card>
    </Modal>
  );
};

// markdown渲染函数
const renderMarkdown = (content: string) => {
  // 渲染markdown并处理图片标签，添加自适应样式
  const renderedHtml = md.render(content);
  const processedHtml = renderedHtml.replace(
    /<img([^>]*)>/g,
    '<img$1 style="max-width: 100%; height: auto; border-radius: 8px; display: block; margin: 8px 0;">'
  );
  
  return (
    <Typography>
      <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
    </Typography>
  );
};

// 修改roles定义，将needtyping的使用移动到AppContent组件内
const roles = (needtyping: boolean): GetProp<typeof Bubble.List, "roles"> => ({
  ai: {
    placement: "start",
    typing: needtyping ? { step: 5, interval: 20 } : false,
    styles: {
      content: {
        borderRadius: 16,
      },
    },
    messageRender: renderMarkdown,
  },
  local: {
    placement: "end",
    variant: "shadow",
  },
});

const UserPage = () => {
  // ==================== Style ====================
  const { styles } = useAppStyles();
  const navigate = useNavigate();

  // ==================== State ====================
  const [headerOpen, setHeaderOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [content, setContent] = useState("");
  const [conversationsItems, setConversationsItems] = useState<
    ConversationItem[]
  >(defaultConversationsItems);
  const [activeKey, setActiveKey] = useState(defaultConversationsItems[0].key);
  const [attachedFiles, setAttachedFiles] = useState<
    GetProp<typeof Attachments, "items">
  >([]);
  // 使用useState和useRef共同管理conversationId
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // 添加一个状态来追踪是否收到了第一个数据块
  const [receivedFirstChunk, setReceivedFirstChunk] = useState(false);
  // 添加AbortController引用
  const abortControllerRef = useRef<AbortController | null>(null);
  // 添加messagesRef来存储最新的messages
  const messagesRef = useRef<any[]>([]);

  // 将needtyping的状态定义移到组件内部
  const [needtyping, setNeedtyping] = useState(true);

  // 问题模式相关状态
  const [isQuestionMode, setIsQuestionMode] = useState(false);
  const [questionContent, setQuestionContent] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState<QuestionData | null>(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  
  // 添加空消息状态
  const [isEmpty, setIsEmpty] = useState(true);

  // 添加滚动到底部的函数
  const scrollToBottom = () => {
    // 首先尝试找到Bubble.List内部的滚动容器
    const bubbleList = document.querySelector('.ant-bubble-list');
    const messageContainer = bubbleList || document.querySelector(`.${styles.messages}`);
    
    if (!messageContainer) {
      return;
    }

    const supportsSmooth = 'scrollBehavior' in document.documentElement.style;

    messageContainer.scrollTo({
      top: messageContainer.scrollHeight, 
      behavior: supportsSmooth ? 'smooth' : 'auto',
    });
  };

  // 保持ref与state同步
  useEffect(() => {
    conversationIdRef.current = conversationId;
    console.log("conversationId状态已更新:", conversationId);

    // 如果conversationId存在且activeKey有效，更新conversationsItems中对应项的conversationId
    if (conversationId && activeKey) {
      setConversationsItems((prev) =>
        prev.map((item) =>
          item.key === activeKey ? { ...item, conversationId } : item
        )
      );
      console.log(
        `已将conversationId ${conversationId} 绑定到对话 ${activeKey}`
      );
    }
  }, [conversationId]);

  // ==================== Runtime ====================
  const [agent] = useXAgent({
    request: async (info, callbacks) => {
      const { messages, message } = info;
      const { onSuccess, onUpdate, onError } = callbacks;

      // current message
      console.log("message", message);

      // history messages
      console.log("messages", messages);

      let fullContent = "";
      let isQuestionModeDetected = false;
      try {
        // 在开始请求时重置接收状态
        setReceivedFirstChunk(false);
        setIsQuestionMode(false);
        setQuestionContent("");

        // 创建新的AbortController并保存到ref
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        // 使用ref获取最新的conversationId值
        const currentConversationId = conversationIdRef.current;
        console.log("当前conversation_id (从ref获取):", currentConversationId);

        // 构建URL，如果有conversationId则添加为查询参数
        const url = currentConversationId 
          ? `https://api2.ibase.site/v3/chat?conversation_id=${currentConversationId}`
          : "https://api2.ibase.site/v3/chat";

        console.log("请求URL:", url);

        // 准备请求体
        const requestBody: any = {
          bot_id: "7581042651714797606",
          user_id: "10086",
          stream: true,
          auto_save_history: true,
          additional_messages: [{ role: "user", content: message }],
        };

        console.log("完整请求体:", JSON.stringify(requestBody));

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal, // 添加signal用于中断请求
        });

        if (!res.ok) {
          throw new Error(`API返回错误：${res.status} ${res.statusText}`);
        }

        for await (const chunk of XStream({ readableStream: res.body! })) {
          // 检查是否已被中断
          if (signal.aborted) {
            console.log("请求已被中断");
            break;
          }

          if (chunk.event === "conversation.chat.created") {
            console.log("收到chat创建事件:", chunk.data);
            try {
              const chatData = JSON.parse(chunk.data);
              if (chatData && chatData.conversation_id) {
                console.log("更新conversation_id:", chatData.conversation_id);
                // 更新ref和state
                conversationIdRef.current = chatData.conversation_id;
                setConversationId(chatData.conversation_id);
              }
            } catch (e) {
              console.error("解析创建事件数据失败:", e);
            }
          }

          // 检查是否有数据字段
          if (chunk.data) {
            // 检查不同的可能格式
            if (chunk.event === "conversation.message.delta") {
              try {
                const deltaData = JSON.parse(chunk.data);
                deltaData.content = deltaData.content.replace(
                  "\\n\\n",
                  "\n\n  "
                );
                fullContent += deltaData.content;
                console.log("fullContent", fullContent);
                
                // 检测是否是问题模式
                if (!isQuestionModeDetected && fullContent.startsWith('<Question>')) {
                  isQuestionModeDetected = true;
                  setIsQuestionMode(true);
                  console.log("检测到问题模式");
                  // 立即显示"正在思考"消息
                  onUpdate("正在思考问题...");
                }

                // 标记已收到第一个数据块
                if (!receivedFirstChunk) {
                  setReceivedFirstChunk(true);
                }

                // 如果是问题模式，缓存内容但仍显示"正在思考"
                if (isQuestionModeDetected) {
                  setQuestionContent(fullContent);
                  // 继续显示"正在思考"状态，不更新显示内容
                } else {
                  onUpdate(fullContent);
                }
              } catch (e) {
                console.error("解析delta数据失败:", e);
              }
            }
          }
        }

        if (!fullContent) {
          console.warn("未收到任何回复内容，返回默认回复");
          fullContent = "抱歉，我暂时无法回答您的问题。请稍后再试。";
        }

        //console.log("完整回复内容:", fullContent);
        console.log("请求完成, conversation_id:", conversationIdRef.current);

        // 如果是问题模式，解析并显示问题
        if (isQuestionModeDetected && fullContent.includes('</Question>')) {
          const questionData = parseQuestionContent(fullContent);
          if (questionData) {
            setCurrentQuestion(questionData);
            setShowQuestionModal(true);
            // 在问题模式下，显示问题文本而非占位符
            onSuccess(questionData.question);
          } else {
            console.error("解析问题失败");
            onSuccess("问题格式解析失败，请重试。");
          }
        } else {
          // 如果请求被中断，只更新到目前为止的内容，但不标记为成功完成
          onSuccess(fullContent);
          
          // 检测是否包含预问诊报告（在onSuccess之后，确保消息已添加）
          if (fullContent.includes('----预问诊报告----')) {
            console.log('检测到预问诊报告，准备提交咨询记录');
            // 使用 setTimeout 确保消息已经添加到列表中
            setTimeout(() => {
              submitInquiryReport(fullContent);
            }, 100);
          }
        }
      } catch (error) {
        // 如果是AbortError，不将其视为错误
        if ((error as any).name === "AbortError") {
          console.log("请求被用户中断");
          onSuccess(fullContent);
          onError(error as Error);
        } else {
          // 处理其他错误
          console.error("API请求失败:", error);
          onError(error as Error);
        }
      } finally {
        // 清理AbortController
        abortControllerRef.current = null;
      }
    },
  });

  const { onRequest, messages, setMessages } = useXChat({
    agent,
  });

  useEffect(() => {
    if (activeKey !== undefined) {
      // 查找对应的会话项
      const activeConversation = conversationsItems.find(
        (item) => item.key === activeKey
      );

      // 如果找到了会话项且它有conversationId
      if (activeConversation && activeConversation.conversationId) {
        console.log(
          `切换到现有对话 ${activeKey}，关联的conversationId是 ${activeConversation.conversationId}`
        );
        // 更新当前conversationId
        setConversationId(activeConversation.conversationId);
        conversationIdRef.current = activeConversation.conversationId;

        // 获取历史消息
        fetchHistoryMessages(activeConversation.conversationId);
      } else {
        // 如果是新对话或没有conversationId的对话
        setMessages([]);
        setNeedtyping(true);
        // 重置conversationId的state和ref
        setConversationId(null);
        conversationIdRef.current = null;
        console.log("新建对话或切换到无历史的对话，重置conversation_id为null");
      }
    }
  }, [activeKey]);

  // 获取历史消息的函数
  const fetchHistoryMessages = async (conversationId: string) => {
    try {
      console.log(`正在获取对话 ${conversationId} 的历史消息...`);

      const response = await fetch(
        `https://api2.ibase.site/v1/conversation/message/list?conversation_id=${conversationId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `获取历史消息失败：${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("历史消息API返回数据:", data);

      // 定义消息类型
      interface HistoryMessageItem {
        id: string;
        content: string;
        role: string;
        created_at: number;
        content_type?: string;
        [key: string]: any;
      }

      // 确保返回了有效数据
      if (
        data &&
        data.data &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        // 根据API返回的数据结构处理历史消息
        // 注意：API返回的消息顺序可能是倒序的，需要进行排序
        const historyMessages = data.data
          .sort(
            (a: HistoryMessageItem, b: HistoryMessageItem) =>
              a.created_at - b.created_at
          ) // 按时间顺序排序
          .filter((msg: HistoryMessageItem) => !msg.content_type || msg.content_type === 'text') // 只显示文本类型消息
          .map((msg: HistoryMessageItem) => ({
            id: msg.id,
            message: msg.content,
            status: msg.role === "user" ? "local" : "done",
          }));

        console.log("处理后的历史消息:", historyMessages);

        // 更新消息列表
        setNeedtyping(false);
        setMessages(historyMessages);
        message.success("已加载历史消息");
      } else {
        console.log("没有找到历史消息或数据格式不正确");
        setMessages([]);
        setNeedtyping(false);
        message.info("没有找到历史消息");
      }
    } catch (error) {
      console.error("获取历史消息失败:", error);
      message.error("获取历史消息失败");
    }
  };

  useEffect(() => {
    // 初始化时重置conversationId的state和ref
    setConversationId(null);
    conversationIdRef.current = null;
    console.log("组件初始化，重置conversation_id为null");
  }, []);

  // 添加监听消息列表变化的useEffect
  useEffect(() => {
    setIsEmpty(messages.length === 0);
    // 同步更新messagesRef，确保在闭包中能访问到最新的messages
    messagesRef.current = messages;
  }, [messages]);

  // 提交咨询记录的函数
  const submitInquiryReport = async (reportContent: string) => {
    try {
      // 解析报告内容
      const patientNameMatch = reportContent.match(/患者姓名:\s*(.+)/);
      const patientReportMatch = reportContent.match(/患者报告:\s*([\s\S]+?)(?=您的报告|$)/);

      if (!patientNameMatch || !patientReportMatch) {
        console.error('无法解析报告内容');
        message.error('报告格式错误，无法提交');
        return;
      }

      const patientName = patientNameMatch[1].trim();
      const patientReport = patientReportMatch[1].trim();

      console.log('解析出的患者姓名:', patientName);
      console.log('解析出的患者报告:', patientReport);

      // 获取token
      const token = localStorage.getItem('dakang_token');
      if (!token) {
        console.error('未找到用户token，请先登录');
        message.warning('请先登录后再进行问诊');
        navigate('/login');
        return;
      }

      // 从ref中获取最新的消息列表，直接在本地构建聊天历史字符串
      const currentMessages = messagesRef.current;
      const chatHistory = currentMessages
        .map(msg => `${msg.status === 'local' ? '用户' : 'AI医生'}: ${msg.message}`)
        .join('\n\n');

      console.log('构建聊天历史，消息数量:', currentMessages.length);
      console.log('聊天历史长度:', chatHistory.length);

      // 调用后端接口
      console.log('正在提交咨询记录...');
      const response = await fetch('https://dk-backend.ibase.site/api/v1/addinquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          patient_name: patientName,
          patient_report: patientReport,
          chat_history: chatHistory,
        }),
      });

      const result = await response.json();
      console.log('提交咨询记录响应:', result);

      if (result.status === 200) {
        message.success('问诊报告已成功提交！');
        console.log('咨询记录提交成功');
      } else {
        message.error(result.msg || '提交失败，请重试');
        console.error('提交失败:', result);
      }
    } catch (error) {
      console.error('提交咨询记录失败:', error);
      message.error('网络错误，提交失败');
    }
  };

  // ==================== Event ====================
  const onSubmit = (nextContent: string) => {
    if (!nextContent) return;

    // 检查当前会话是否有消息，如果没有则更新会话标题为消息前10个字
    if (messages.length === 0 && activeKey !== undefined) {
      // 截取消息的前10个字作为标题，如果不足10个字则全部使用
      const newLabel =
        nextContent.length > 10
          ? nextContent.substring(0, 10) + "..."
          : nextContent;

      // 更新conversationsItems中当前会话的label
      setConversationsItems((prev) =>
        prev.map((item) =>
          item.key === activeKey ? { ...item, label: newLabel } : item
        )
      );
    }

    onRequest(nextContent);
    setContent("");
    // 在移动端提交后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
    // 提交后稍等一下再滚动，确保消息已添加
    setTimeout(scrollToBottom, 50);
  };

  const onPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (info) => {
    // 检查当前会话是否有消息，如果没有则更新会话标题为提示词内容的前10个字
    if (messages.length === 0 && activeKey !== undefined) {
      const description = info.data.description as string;
      // 截取提示词内容的前10个字作为标题
      const newLabel =
        description.length > 10
          ? description.substring(0, 10) + "..."
          : description;

      // 更新conversationsItems中当前会话的label
      setConversationsItems((prev) =>
        prev.map((item) =>
          item.key === activeKey ? { ...item, label: newLabel } : item
        )
      );
    }

    onRequest(info.data.description as string);
    // 在移动端点击提示词后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
    // 点击提示词后稍等一下再滚动，确保消息已添加
    setTimeout(scrollToBottom, 50);
  };

  const onSenderPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (
    info
  ) => {
    let found = false;
    for (let i = 0; i < senderPromptsItems.length; i++) {
      if (
        content.indexOf(`@${senderPromptsItems[i].description as string}, `) !==
        -1
      ) {
        setContent(
          content.replace(
            `@${senderPromptsItems[i].description as string}, `,
            `@${info.data.description as string}, `
          )
        );
        found = true;
        console.log("替换成功");
        break;
      }
    }
    if (!found) {
      setContent(`@${info.data.description as string}, ` + content);
    }
    // 在移动端点击提示词后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onAddConversation = () => {
    handleCancelRequest();
    setConversationId(null);
    setConversationsItems([
      ...conversationsItems,
      {
        key: `${conversationsItems.length}`,
        label: `新问诊对话`,
      },
    ]);
    setActiveKey(`${conversationsItems.length}`);
    setMessages([]);
    setNeedtyping(true);
    // 在移动端添加会话后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onConversationClick: GetProp<typeof Conversations, "onActiveChange"> = (
    key
  ) => {
    handleCancelRequest();
    setActiveKey(key);
    // 在移动端选择会话后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const handleFileChange: GetProp<typeof Attachments, "onChange"> = (info) =>
    setAttachedFiles(info.fileList);

  // 处理登录按钮点击
  const handleLoginClick = () => {
    navigate("/login");
  };

  // 添加中断请求的函数
  const handleCancelRequest = () => {
    if (abortControllerRef.current) {
      console.log("用户取消请求");
      abortControllerRef.current.abort();
    }
  };

  // 处理问题选项点击
  const handleOptionClick = (option: string) => {
    setShowQuestionModal(false);
    setCurrentQuestion(null);
    // 将选项作为新消息提交
    onSubmit(option);
  };

  // 关闭问题模态框
  const handleCloseQuestion = () => {
    setShowQuestionModal(false);
    setCurrentQuestion(null);
  };

  // 处理手动输入
  const handleManualInput = () => {
    setShowQuestionModal(false);
    setCurrentQuestion(null);
    // 关闭问题模态框，用户可以在输入框中手动输入答案
  };

  // ==================== Nodes ====================
  // 将欢迎信息改为气泡形式
  const placeholderNode = {
    key: 'welcome-message',
    role: 'ai',
    content: '你好，我是达康AI医生。我将帮助您进行就医前的预问诊，稍后我会询问您的姓名、症状，并针对性地提出一系列问诊问题，这些信息将供医生查阅，帮助医生更好地了解您的情况。请问您的姓名是？'
  };

  const items: GetProp<typeof Bubble.List, "items"> = [
    placeholderNode,
    ...messages.map(({ id, message, status }) => ({
      key: id,
      role: status === "local" ? "local" : "ai",
      content: message,
    })),
    // 只有在agent请求中且尚未收到第一个数据块时才显示loading状态
    ...(agent.isRequesting() && !receivedFirstChunk
      ? [
          {
            key: "loading-message",
            role: "ai",
            content: "",
            loading: true,
          },
        ]
      : []),
  ];

  // console.log("当前消息列表:", messages);
  // console.log("气泡项:", items);
  // console.log("是否收到第一个数据块:", receivedFirstChunk);

  // 在组件内部使用roles函数
  const currentRoles = roles(needtyping);

  const attachmentsNode = (
    <Badge dot={attachedFiles.length > 0 && !headerOpen}>
      <Button
        type="text"
        icon={<PaperClipOutlined />}
        onClick={() => setHeaderOpen(!headerOpen)}
      />
    </Badge>
  );

  const senderHeader = (
    <Sender.Header
      title="Attachments"
      open={headerOpen}
      onOpenChange={setHeaderOpen}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={handleFileChange}
        placeholder={(type) =>
          type === "drop"
            ? { title: "Drop file here" }
            : {
                icon: <CloudUploadOutlined />,
                title: "上传文件",
                description: "点击或拖拽文件到此区域上传",
              }
        }
      />
    </Sender.Header>
  );

  const logoNode = (
    <div className={styles.logo}>
      <img src="./dakanglogo.png" draggable={false} alt="logo" />
      <span>达康AI医生</span>
    </div>
  );

  const mobileLogoNode = (
    <div className={styles.mobileLogo}>
      <img src="./dakanglogo.png" draggable={false} alt="logo" />
      <span>达康AI医生</span>
    </div>
  );

  const menuContent = (
    <>
      {logoNode}
      <Button
        onClick={() => navigate("/")}
        type="link"
        className={styles.addBtn}
        icon={<ArrowLeftOutlined />}
      >
        返回主页
      </Button>
      <Button
        onClick={onAddConversation}
        type="link"
        className={styles.addBtn}
        icon={<PlusOutlined />}
      >
        新问诊对话
      </Button>
      <Conversations
        items={conversationsItems}
        className={styles.conversations}
        activeKey={activeKey}
        onActiveChange={onConversationClick}
      />
    </>
  );

  // ==================== Render =================
  return (
    <div className={styles.layout}>
      {/* 问题模态框 */}
      {showQuestionModal && currentQuestion && (
        <QuestionCard
          questionData={currentQuestion}
          onOptionClick={handleOptionClick}
          onClose={handleCloseQuestion}
          onManualInput={handleManualInput}
        />
      )}
      
      {/* 移动端顶部导航 */}
      <div className={styles.mobileHeader}>
        {mobileLogoNode}
        <Space>
          <Button
            type="text"
            icon={<LoginOutlined />}
            onClick={handleLoginClick}
          />
          <Button
            type="text"
            icon={drawerVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={() => setDrawerVisible(!drawerVisible)}
          />
        </Space>
      </div>

      {/* 移动端抽屉菜单 */}
      <Drawer
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        placement="left"
        width={280}
        className={styles.drawerMenu}
        title="达康AI医生"
        footer={null}
      >
        {menuContent}
      </Drawer>

      {/* 桌面端左侧菜单 */}
      <div className={styles.menu}>{menuContent}</div>

      <div className={styles.chat}>
        {/* 🌟 消息列表 */}
        <Bubble.List
          items={items}
          roles={currentRoles}
          className={styles.messages}
        />
        {/* 🌟 提示词 */}
        <Prompts
          items={senderPromptsItems}
          onItemClick={onSenderPromptsItemClick}
        />
        {/* 🌟 输入框 */}
        <Sender
          value={content}
          header={senderHeader}
          onSubmit={onSubmit}
          onChange={setContent}
          prefix={attachmentsNode}
          loading={agent.isRequesting()}
          className={styles.sender}
          onCancel={handleCancelRequest} // 添加取消回调
          placeholder="点此给达康AI医生发送消息"
        />
      </div>
    </div>
  );
};

export default UserPage;
